import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { submitSignupRequest } from "@/lib/api";
import { AuthLayout, AuthNotice, BackToSignIn, PanelBlock, APP_NAME } from "@/components/auth/AuthLayout";

export default function SignUpPage() {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = `Sign up | ${APP_NAME}`;
    return () => { document.title = APP_NAME; };
  }, []);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !phone.trim()) return;

    setStatus("loading");
    setMessage("");
    try {
      await submitSignupRequest({
        full_name: fullName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
      });
      setStatus("success");
      setMessage("Thank you. We'll be in touch shortly.");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <AuthLayout
      title="Request access"
      subtitle="Leave your details and we'll set up your clinic account with you."
      back={<BackToSignIn />}
      panel={
        <PanelBlock eyebrow="For clinics and hospitals" title={<>Get started with<br />{APP_NAME}.</>}>
          <p>Request access and our team will set up your clinic account. You'll be up and running in minutes.</p>
        </PanelBlock>
      }
      footer={
        <p>
          Already have an account?{" "}
          <Link to="/login" className="touch-target font-medium text-primary hover:underline">Sign in</Link>
        </p>
      }
    >
      {status === "success" ? (
        <AuthNotice tone="success">{message}</AuthNotice>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fullName">Full name</Label>
            <Input
              ref={nameInputRef}
              id="fullName"
              type="text"
              placeholder="Dr. Priya Sharma"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={status === "loading"}
              required
              autoComplete="name"
              className="h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone number</Label>
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              placeholder="10-digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={status === "loading"}
              required
              autoComplete="tel"
              aria-describedby="phone-hint"
              className="h-11"
            />
            <p id="phone-hint" className="text-xs text-muted-foreground">We'll use this to reach you.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">
              Email <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@clinic.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "loading"}
              autoComplete="email"
              className="h-11"
            />
          </div>

          {status === "error" && message && <AuthNotice tone="error">{message}</AuthNotice>}

          <Button
            type="submit"
            disabled={status === "loading" || !fullName.trim() || !phone.trim()}
            className="w-full h-11"
          >
            {status === "loading" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Request access"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
